// TRAC-998: where /embed addresses the messages it posts to its parent page.
//
// postMessage silently drops a message whose targetOrigin does not match the
// parent. Prod used to hard-code https://www.tirerack.com, which is right for
// customers but wrong for our internal site: https://traci.mi.tirerack.com frames
// /embed from its own origin, so TRACI_UI_READY never arrived and the chat sat on
// its loading skeleton forever (dev/QA post to '*', which is why only prod broke).
//
// The rule:
//   - non-prod: '*', exactly as before.
//   - prod: the parent's real origin when it is on the exact-match allowlist,
//     otherwise https://www.tirerack.com (today's behaviour, so tirerack.com cannot
//     regress when the parent can't be told). Never '*' in prod, never a suffix
//     match — a look-alike origin gets the tirerack.com address and the browser
//     drops the message.
//
// The parent is read from location.ancestorOrigins (authoritative, Chromium/WebKit)
// and falls back to document.referrer (browsers without ancestorOrigins). The
// referrer fallback assumes two things that hold today: /embed never navigates
// itself inside the frame (a reload keeps the referrer; a self-navigation would
// make the referrer /embed's own origin), and the internal site sends no
// Referrer-Policy that strips it (a stripped referrer lands on the tirerack.com
// fallback, which is the TRAC-998 symptom again for those browsers).
//
// `_seam` is the same mockable indirection embedGating.ts uses: jsdom's location
// and referrer are not configurable, and vi.spyOn on a bare ESM export would not
// reach in-module callers.

export const PROD_PARENT_ORIGINS: readonly string[] = [
  'https://www.tirerack.com',
  'https://traci.mi.tirerack.com',
];

const PROD_FALLBACK_ORIGIN = 'https://www.tirerack.com';

export const _seam = {
  getAncestorOrigins: (): ArrayLike<string> | undefined =>
    typeof window === 'undefined'
      ? undefined
      : (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins,
  getReferrer: (): string => (typeof document === 'undefined' ? '' : document.referrer),
};

function resolveParentOrigin(): string | null {
  try {
    const ancestors = _seam.getAncestorOrigins();
    if (ancestors && ancestors.length > 0) return ancestors[0];
  } catch {
    /* fall through to the referrer */
  }
  try {
    const referrer = _seam.getReferrer();
    return referrer ? new URL(referrer).origin : null;
  } catch {
    return null;
  }
}

export function parentTargetOrigin(env: string): string {
  if (env !== 'prod') return '*';
  const parent = resolveParentOrigin();
  return parent !== null && PROD_PARENT_ORIGINS.includes(parent) ? parent : PROD_FALLBACK_ORIGIN;
}
